import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type RowSelectionState,
} from '@tanstack/react-table';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Amount, Ltr } from '@/components/amount';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';

import type { ColumnLabels, DocumentCardModel } from './types';

/**
 * Table mode: the SAME rows the cards draw, as a full-width TanStack table
 * with the card's columns — code, who, (detail), amount, status — a row click
 * selecting exactly as a card click does. With `bulkActions` the table grows a
 * checkbox column, and the slot is drawn above it while anything is ticked.
 *
 * Every cell is a `<TableCell>`, so BR-ADM-08-07's density setting governs
 * this table as it governs every other.
 */
export function DocumentTable<Row extends { id: string }>({
  rows,
  card,
  labels,
  selectedId,
  onSelect,
  bulkActions,
}: {
  rows: readonly Row[];
  card: (row: Row) => DocumentCardModel;
  labels: ColumnLabels | undefined;
  selectedId: string | null;
  onSelect: (id: string) => void;
  bulkActions?: ((rows: readonly Row[], clear: () => void) => ReactNode) | undefined;
}) {
  const { t } = useTranslation();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const selectable = bulkActions !== undefined;
  const hasMeta = rows.some((row) => {
    const meta = card(row).meta;
    return meta !== undefined && meta !== null;
  });

  const columns = useMemo(() => {
    const helper = createColumnHelper<Row>();
    const defs: ColumnDef<Row, unknown>[] = [];
    if (selectable) {
      defs.push(
        helper.display({
          id: 'select',
          header: ({ table }) => (
            <Checkbox
              aria-label={t('doclist.selectAll')}
              checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
              onCheckedChange={(value) => table.toggleAllPageRowsSelected(value === true)}
            />
          ),
          cell: ({ row }) => (
            <Checkbox
              aria-label={t('doclist.selectRow', { code: card(row.original).code })}
              checked={row.getIsSelected()}
              onCheckedChange={(value) => row.toggleSelected(value === true)}
              onClick={(event) => event.stopPropagation()}
            />
          ),
        }),
      );
    }
    defs.push(
      helper.display({
        id: 'code',
        header: () => t(labels?.codeKey ?? 'doclist.colCode'),
        cell: ({ row }) => <Ltr value={card(row.original).code} className="font-mono" />,
      }),
      helper.display({
        id: 'title',
        header: () => t(labels?.titleKey ?? 'doclist.colName'),
        cell: ({ row }) => <span className="font-medium">{card(row.original).title}</span>,
      }),
    );
    if (hasMeta) {
      defs.push(
        helper.display({
          id: 'meta',
          header: () => t(labels?.metaKey ?? 'doclist.colDetail'),
          cell: ({ row }) => card(row.original).meta ?? null,
        }),
      );
    }
    defs.push(
      helper.display({
        id: 'amount',
        header: () => <span className="block text-end">{t(labels?.amountKey ?? 'doclist.colAmount')}</span>,
        cell: ({ row }) => {
          const amount = card(row.original).amount;
          return amount ? (
            <span className="block text-end">
              <Amount value={amount.value} currency={amount.currency} />
            </span>
          ) : null;
        },
      }),
      helper.display({
        id: 'status',
        header: () => t(labels?.statusKey ?? 'doclist.colStatus'),
        cell: ({ row }) => card(row.original).chip,
      }),
    );
    return defs;
  }, [t, card, labels, selectable, hasMeta]);

  const table = useReactTable({
    data: rows as Row[],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: selectable,
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
  });

  const ticked = table.getSelectedRowModel().rows.map((row) => row.original);
  const clear = () => setRowSelection({});

  return (
    <div className="grid gap-3">
      {selectable && ticked.length > 0 ? (
        <div
          role="region"
          aria-label={t('doclist.selectedCount', { n: ticked.length })}
          className="flex flex-wrap items-center gap-2 rounded-md bg-muted/60 px-3 py-2 text-sm"
        >
          <span className="me-auto tabular-nums">{t('doclist.selectedCount', { n: ticked.length })}</span>
          {bulkActions(ticked, clear)}
        </div>
      ) : null}
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              className={cn('cursor-pointer', row.original.id === selectedId && 'bg-primary/5')}
              data-state={row.original.id === selectedId ? 'selected' : undefined}
              aria-selected={row.original.id === selectedId}
              onClick={() => onSelect(row.original.id)}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
